// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { convNum, toInt } from '../../utils';

@Injectable({ providedIn: 'root' })
export class HceSvParserService {
  private readonly defaultRefNameMap = {
    '1': 'chr1',
    '2': 'chr2',
    '3': 'chr3',
    '4': 'chr4',
    '5': 'chr5',
    '6': 'chr6',
    '7': 'chr7',
    '8': 'chr8',
    '9': 'chr9',
    '10': 'chr10',
    '11': 'chr11',
    '12': 'chr12',
    '13': 'chr13',
    '14': 'chr14',
    '15': 'chr15',
    '16': 'chr16',
    '17': 'chr17',
    '18': 'chr18',
    '19': 'chr19',
    '20': 'chr20',
    '21': 'chr21',
    '22': 'chr22',
    '23': 'chrX',
    '24': 'chrY',
  } as Record<string, string>;

  private readonly defaultSvColors = {
    'Deletion': 'rgb(79,129,189)',
    'Insertion': 'rgb(192,80,77)',
    'Inversion': 'rgb(155,187,89)',
    'Translocation': 'rgb(162,40,255)',
    'Duplication': 'rgb(116,192,242)',
    'other': 'rgb(221,221,221)',
  } as Record<string, string>;

  parse(hsv: any, refNameMap?: Record<string, string>, svColors?: Record<string,string>): any[] {
    try {
      if (!hsv) {
        return [];
      }

      const version = Number(hsv.version);
      if (!Number.isInteger(version) || version <= 12) {
        return [];
      }

      const mapped = refNameMap ?? this.defaultRefNameMap;
      const colors = svColors ?? this.defaultSvColors;

      const isTranslocation = <T>(type: unknown, fn: () => T, fallback: T): T =>
        type === 'Translocation' ? fn() : fallback;

      const svs = hsv.svs as Record<string, any>;
      return Object.entries(svs).map(([svId, sv]) => {
        const result: any = {
          flagStart: toInt(sv.region.chr.start_index, 0),
          flagEnd: toInt(sv.region.chr.end_index, 0),
          start: convNum(sv.region.chr.start.min, 0),
          end: convNum(sv.region.chr.end.max, 0),
          target_from_idx: toInt(sv.region.contig.start_index, 0),
          target_to_idx: toInt(sv.region.contig.end_index, 0),
          conStart: convNum(sv.region.contig.start.min, 0),
          conEnd: convNum(sv.region.contig.end.max, 0),
          length: convNum(sv.length, 0),
          SVRefSize: convNum(sv.region.chr.length, 0),
          SVConSize: convNum(sv.region.contig.length, 0),
          noOfChr: sv.chr_id ? parseInt(sv.chr_id, 10) : '-',
          mappedRefName: sv.chr_id ? String(mapped[sv.chr_id] ?? '') : '',
          chromosome: toInt(sv.chr_id, '-'),
          alignmentFileName: toInt(sv.chr_id, '-'),
          SVName: sv.contig_id ? String(sv.contig_id) : '',
          SVID: svId,
          QClevel: '',
          embeddedFlg: sv.Embedded === 'Y' ? 'Y' : '',
          type: sv.type ? String(sv.type) : '',
          Zygosity: sv.zygosity ? String(sv.zygosity) : '',
          quality_score: sv.quality_score ?? (sv.confidence ?? ''),
          reason: sv.reason ? String(sv.reason) : '',
          flags: sv.Embedded === 'Y' ? 'Embedded_Y' : '0',
          orientation: 'N',
          RefStartAlignmentUUID: '',
          RefEndAlignmentUUID: '',
          geneName: this.makeGenesNameLinks(sv.region.chr.affected_genes, sv.region.chr.affected_genes_hrefs),
          geneNameOrg: sv.region.chr.affected_genes ?? [],
          flagStart2: isTranslocation(sv.type, () => toInt(sv.segment_to.start_index, 0), 0),
          flagEnd2: isTranslocation(sv.type, () => toInt(sv.segment_to.end_index, 0), 0),
          start2: isTranslocation(sv.type, () => convNum(sv.segment_to.start.min, 0), 0),
          end2: isTranslocation(sv.type, () => convNum(sv.segment_to.end.max, 0), 0),
          conStart2: 0,
          conEnd2: 0,
          length2: isTranslocation(sv.type, () => convNum(sv.segment_to.length, 0), 0),
          refName2: isTranslocation(sv.type, () => toInt(sv.chr_to_id, '-'), '-'),
          noOfChr2: isTranslocation(sv.type, () => toInt(sv.chr_to_id, '-'), '-'),
          mappedRefName2: isTranslocation(sv.type, () => (sv.chr_to_id ? String(mapped[sv.chr_to_id] ?? '') : ''), ''),
          geneName2: isTranslocation(sv.type, () => this.makeGenesNameLinks(sv.segment_to.affected_genes, sv.segment_to.affected_genes_hrefs), []),
          geneNameOrg2: isTranslocation(sv.type, () => sv.segment_to.affected_genes ?? [], []),
          color: String(colors[sv.type] ?? colors['other']),
          genome_region_names: sv.genome_region_names ?? '',
          inverted: sv.inverted ? 'true' : 'false',
          minIntervalDepth: sv.region_frags_info?.region_aggregated_stats?.coverage?.min ?? '',
          medianIntervalDepth: sv.region_frags_info?.region_aggregated_stats?.coverage?.median ?? '',
          region_chr_end_min: convNum(sv.region.chr.end.min, 0),
          region_chr_is_clear: sv.region.chr.is_clear ? 'true' : 'false',
          region_chr_start_max: convNum(sv.region.chr.start.max, 0),
          region_chr_affected_regions: sv.region.chr.affected_regions ?? [],
          region_chr_affected_genes_hrefs: sv.region.chr.affected_genes_hrefs ?? [],
          region_contig_end_min: convNum(sv.region.contig.end.min, 0),
          region_contig_is_clear: sv.region.contig.is_clear ? 'true' : 'false',
          region_contig_start_max: convNum(sv.region.contig.start.max, 0),
          region_next_al_id: convNum(sv.region.next_al_id, 0),
          region_prev_al_id: convNum(sv.region.prev_al_id, 0),
          segment_to_end_min: isTranslocation(sv.type, () => convNum(sv.segment_to.end.min, 0), 0),
          segment_to_is_clear: isTranslocation(sv.type, () => (sv.segment_to.is_clear ? 'true' : 'false'), ''),
          segment_to_start_max: isTranslocation(sv.type, () => convNum(sv.segment_to.start.max, 0), 0),
          segment_to_affected_regions: isTranslocation(sv.type, () => sv.segment_to.affected_regions ?? [], []),
          segment_to_affected_genes_hrefs: isTranslocation(sv.type, () => sv.segment_to.affected_genes_hrefs ?? [], []),
          hce_sv_id: sv.hce_sv_id ?? '',
          molCheck: (sv.lytx?.mol_check ?? '') === '' ? '-' : (sv.lytx.mol_check ? 'pass' : 'fail'),
          coLoRSdBControlDb: sv.lytx?.percent_presence_lrs_cohort ?? '',
          gnomADControlDb: sv.lytx?.percent_presence_gnomad_cohort ?? '',
          masked: (sv.lytx?.masked ?? '') === '' ? '-' : (sv.lytx.masked ? 'true' : 'false'),
          clusterRepresentativeSv: sv.lytx?.rep_sv_in_cluster === undefined ? '-' : (sv.lytx?.rep_sv_in_cluster ? 'true' : 'false'),
        };

        const ontCohort = sv.lytx?.percent_presence_ont_cohort;
        if (ontCohort !== undefined && ontCohort !== null && ontCohort !== '') {
          result.lytx_ont_cohort = ontCohort;
        }

        return result;
      });
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  private makeGenesNameLinks(genes?: any[], genes_hrefs?: any[]): any[] {
    const arr: any[] = [];
    if (genes) {
      for (let i = 0; i < genes.length; i++) {
        arr.push(genes[i]);
        arr.push(genes_hrefs?.[i] ?? '');
      }
    }
    return arr;
  }
}
